import { intersectionObserver } from './intersectionObserver.js'
import { rerenderGrid } from './grid.js'

export function addKeyboardShortcutEvents() {
  const frameWindow = this.node.contentWindow
  const frameDocument = frameWindow.document
  const Canvas = this.parent

  const canvasScope = (frameWindow.scope = this.parent.scope)
  canvasScope.positions ||= {}
  canvasScope.window = frameWindow

  // The full smbls namespace is only a debug/bridge global inside the editor
  // iframe — assign it lazily so this file (eager via the shared barrel)
  // doesn't drag the framework bundle into the eager chunk. index.js warms
  // this chunk at entry, so the assignment resolves ~immediately.
  import('smbls')
    .then((m) => {
      frameWindow._smbls = m
    })
    .catch(() => {})
  frameWindow.Canvas = Canvas
  frameWindow.CanvasData = Canvas.scope
  frameWindow.rerenderGrid = rerenderGrid
  frameWindow.isActiveNodeSelectable = (doc, frameDoc) =>
    this.call('isActiveNodeSelectable', doc, frameDoc)
  frameWindow.platformState = this.getRootState()
  frameWindow.router = this.router
  frameWindow.intersectionObserver = intersectionObserver(frameWindow)

  const escapeKey = (event) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.setCanvasMode(false)
      this.activateContext(null)
      this.call('setFloatingPanel')
      // Code to execute when ESC is pressed
      if (this.getRootState('activeModal')) {
        this.closeModal()
      } else if (canvasScope.docs) {
        this.closeWindow('docs')
      } else if (canvasScope.chosen) {
        this.activateChosen(null)
      } else if (!this.isCanvasMode('defaultMode')) {
        this.setCanvasMode('defaultMode')
      } else if (canvasScope.componentEditor) {
        this.closeWindow('componentEditor')
      } else if (frameWindow.scope.selected) {
        this.activateSelected(null)
      } else if (canvasScope.explorer) {
        this.closeWindow('explorer')
      }
    }
  }

  const enterKey = (event) => {
    // Code to execute when ENTER is pressed
    if (event.key === 'Enter' || event.keyCode === 13) {
      const isSelectable = this.call('isActiveNodeSelectable', document, frameDocument)
      if (!isSelectable) {
        if (canvasScope.selected) {
          this.activateChosen(canvasScope.selected)
        } else if (canvasScope.hovered && this.getRootState('isModifierPressed')) {
          this.activateSelected(canvasScope.hovered)
          this.activateChosen(canvasScope.hovered)
        } else if (canvasScope.hovered) {
          this.activateChosen(canvasScope.hovered)
        }
        setTimeout(() => {
          this.setWindow('componentEditor', 'code')
        }, 150)
      }
    }
  }

  const spaceKey = (event) => {
    // Code to execute when SPACE is pressed
    if (event.key === ' ' || event.keyCode === 32) {
      const isSelectable = this.call('isActiveNodeSelectable', document, frameDocument)
      if (!isSelectable) {
        if (canvasScope.chosen) {
          this.activateChosen(null)
        } else if (canvasScope.hovered && this.getRootState('isShiftPressed')) {
          this.activateChosen(canvasScope.hovered)
        } else if (canvasScope.selected) {
          this.activateChosen(canvasScope.selected)
        } else {
          const canvasMode = this.getCanvasMode()
          this.setCanvasMode(
            canvasMode
              ? canvasMode === 'panningMode'
                ? 'defaultMode'
                : canvasMode === 'defaultMode'
                  ? 'inspectMode'
                  : 'panningMode'
              : 'panningMode'
          )
        }
      }
    }
  }

  const deleteKey = async (event) => {
    // Code to execute when DELETE is pressed
    const isDelete = event.key === 'Delete' || event.keyCode === 46
    const isBackspace = event.key === 'Backspace' || event.keyCode === 8

    const isSelectable = this.call('isActiveNodeSelectable', document, frameDocument)
    if (!canvasScope.selected || isSelectable || !(isDelete || isBackspace)) {
      return
    }

    const { activeLayer } = canvasScope
    if (activeLayer) {
      return await this.call('handleActiveLayerDeletion', activeLayer)
    } else if (!canvasScope.chosen) {
      return await this.call('handleComponentDeletion')
    }
  }

  const sKey = (event) => {
    // Code to execute when S is pressed
    if (event.key === 'S' || event.keyCode === 83) {
      const isSelectable = this.call('isActiveNodeSelectable', document, frameDocument)
      if (!isSelectable) {
        this.syncCanvas()
        this.toggleWindow('explorer')
      }
    }
  }

  const eKey = (event) => {
    // Code to execute when E is pressed
    if (event.key === 'E' || event.keyCode === 69) {
      const isSelectable = this.call('isActiveNodeSelectable', document, frameDocument)
      if (!isSelectable) {
        const isCode = this.getWindow('componentEditor') === 'code'
        this.setWindow('componentEditor', isCode ? null : 'code')
      }
    }
  }

  const undoRedoKey = (event) => {
    // Code to execute when Ctrl/Cmd + Z (undo) or Ctrl/Cmd + Shift + Z (redo) is pressed
    const isZKey = event.key?.toLowerCase() === 'z'
    const isModifierPressed = event.ctrlKey || event.metaKey

    if (isModifierPressed && isZKey) {
      event.preventDefault()
      // const changes = await (event.shiftKey ? this.sdk.redo() : this.sdk.undo())

      // not needed anymore after `upcoming` update
      // if (!changes || changes.length === 0) return

      // const keys = changes.map((change) => change[1])
      // if (
      //   keys.some(
      //     (path) => path[0] === 'schema' && path.includes('gridOptions')
      //   )
      // ) {
      //   await this.getCanvasCollection().update()
      // }

      // keys.forEach(
      //   async (path) =>
      //     ['components', 'pages'].includes(path[0]) &&
      //     (await this.updateComponentOnCanvas(this.getItem(path[1])))
      // )

      // await this.getFilesSidebar()?.Overflow.update()
    }
  }

  const toggleKeyEvents = (event) => {
    const isMac = /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent)

    const isShiftKey = event.key === 'Shift'
    const isAltKey = event.key === 'Alt'
    const isModifierKey = isMac
      ? event.key === 'Meta' || event.code === 'MetaLeft' || event.code === 'MetaRight'
      : event.key === 'Control' || event.code === 'ControlLeft' || event.code === 'ControlRight'

    if (event.type === 'keydown') {
      if (isShiftKey) this.getRootState().isShiftPressed = true
      if (isAltKey) this.getRootState().isAltPressed = true
      if (isModifierKey) this.getRootState().isModifierPressed = true
    } else if (event.type === 'keyup') {
      if (isShiftKey) this.getRootState().isShiftPressed = false
      if (isAltKey) this.getRootState().isAltPressed = false
      if (isModifierKey) this.getRootState().isModifierPressed = false
    }

    const isShiftPressed = this.getRootState('isShiftPressed')
    const isAltPressed = this.getRootState('isAltPressed')
    const isModifierPressed = this.getRootState('isModifierPressed')

    const isDefault = this.isCanvasMode('defaultMode')
    if (isModifierPressed) this.setCanvasMode('inspectMode')
    else if (isShiftPressed) this.setCanvasMode('panningMode')
    else this.setCanvasMode('defaultMode')
  }

  // The shell's overlay reader (shared functions/shellOverlayOpen.js): a layer
  // above the canvas — a dialog, a menu, the assistant's main thread over the
  // page — owns the keys, and the editor stands down (Delete and undo would
  // act on a canvas nobody sees). The modifier RELEASE still reaches it.
  const overlayUp = () =>
    typeof this.context?.functions?.shellOverlayOpen === 'function' &&
    !!this.call('shellOverlayOpen')

  const keyDownShortcuts = (event) => {
    if (overlayUp()) return
    const t = setTimeout(() => {
      toggleKeyEvents(event)
      clearTimeout(t)
    }, 1)
  }

  frameDocument.addEventListener('keydown', keyDownShortcuts)
  document.addEventListener('keydown', keyDownShortcuts)

  // Listen for keyup event to detect when keys are released
  const keyUpShortcuts = async (event) => {
    if (
      document.activeElement.tagName === 'TEXTAREA' ||
      document.activeElement.tagName === 'INPUT' ||
      document.activeElement.contentEditable === 'true' ||
      frameDocument.activeElement.tagName === 'TEXTAREA' ||
      frameDocument.activeElement.tagName === 'INPUT' ||
      frameDocument.activeElement.contentEditable === 'true'
    ) {
      // Still need to release modifier keys even in input fields
      toggleKeyEvents(event)
      return
    }
    if (overlayUp()) {
      toggleKeyEvents(event)
      return
    }
    escapeKey(event)
    enterKey(event)
    spaceKey(event)
    sKey(event)
    eKey(event)
    toggleKeyEvents(event)
    await deleteKey(event)
    await undoRedoKey(event)
  }

  frameDocument.addEventListener('keyup', keyUpShortcuts)
  document.addEventListener('keyup', keyUpShortcuts)

  const onCopy = () => {
    if (!canvasScope.selected || this.call('isActiveNodeSelectable', document, frameDocument)) {
      return
    }

    const code = this.call('getCodeToCopy')
    const str = this.call('stringifyCode', code)
    this.call('copyStringToClipboard', str)
    // const encoded = _smbls.encodeNewlines(str)
    // this.call('copyStringToClipboard', encoded)
  }

  frameDocument.addEventListener('copy', onCopy)
  document.addEventListener('copy', onCopy)

  const onPaste = async (ev) => {
    try {
      if (this.call('isActiveNodeSelectable', document, frameDocument)) {
        return
      }

      // clipboardData must be read synchronously during the event — keep it
      // before any await (parseStringCode is async since the bundle-split).
      const clipboardData = ev.clipboardData.getData('text')

      const parts = clipboardData.split('// COMPONENT SEPARATOR')
      const parsedParts = await Promise.all(
        parts.map(async (part) => {
          // USER-CODE-ORIGIN review N4: pasted code stays AS PARSED (function
          // fields are source strings, the stored form) — the shell never
          // evaluates clipboard code; the opaque frame revives it.
          return (await this.parseStringCode(part)) || {}
        })
      )
      const codes = parsedParts.filter((code) => {
        const { metadata, ...copiedValue } = code

        if (metadata !== 'copiedFromSymbols' || !copiedValue.key) {
          return false
        }
        const { key, layerCode } = copiedValue
        const isComponent = Boolean(copiedValue.value && copiedValue.settings?.gridOptions)

        console.log(layerCode, isComponent)
        if (!layerCode && !isComponent) {
          return false
        }

        // const isComponentWithSameKey = key === this.getSelectedKey()
        if (canvasScope.selected && layerCode) {
          // await this.call('handleLayerPaste', key, layerCode, canvasScope)
          // } else if (
          //   canvasScope.selected &&
          //   isComponent &&
          //   !isComponentWithSameKey
          // ) {
          //   // maybe check for extends also
          //   if (canvasScope.selected.state.uses) {
          //     canvasScope.selected.state.uses.push(key)
          //   } else {
          //     canvasScope.selected.state.uses = [key]
          //   }
          // await this.call('handleLayerPaste', key, {}, canvasScope)
        } else if (isComponent) {
          return true
        }

        return false
      })

      console.log(codes)
      this.call(
        'createMultipleItems',
        codes // .map(({ ...value }) => value)
      )
    } catch (error) {
      this.error(error)
      this.call('openNotification', {
        type: 'error',
        title: this.call('polyglot', 'shell.clipboard.pasteFailed.title'),
        message: error
      })
    }
  }

  frameDocument.addEventListener('paste', onPaste)
  document.addEventListener('paste', onPaste)
}
